import * as React from 'react'
import { useEffect, useState } from 'react'

import { Button, Tooltip } from '@buffer-mono/popcorn'

import { useLinkShorteningDisabledOverride } from '~publish/hooks/useLinkShorteningDisabledOverride'
import { changeLinkShortener as changeLinkShortenerThunk } from '~publish/legacy/general-settings/thunks/changeLinkShortener'
import { useAppDispatch } from '~publish/legacy/store'

import { LinkShortenerIcon } from './LinkShortenerIcon'
import { LinkShorteningDomains } from '../../../constants/linkShortening'
import ComposerActionCreators from '../../composer/composer/action-creators/ComposerActionCreators'
import type { Draft } from '../../composer/composer/entities/Draft'
import { trackLinkShortenerToggleClick } from '../tracking'

import styles from './LinkShortenerButton.module.css'

export const LinkShortenerButton = ({
  draft,
  selectedProfileId,
  domainForLinkShorteningSelectedProfile,
}: {
  draft: Draft
  selectedProfileId: string
  domainForLinkShorteningSelectedProfile: string
}): React.JSX.Element | null => {
  const [showMessage, setShowMessage] = useState(false)
  const linkShorteningDisabledOverride = useLinkShorteningDisabledOverride()
  const hasBeenRendered = React.useRef(false)
  const dispatch = useAppDispatch()

  // This is to prevent the link shortening from being enabled for free users
  // when the toggle was on, and post is empty + override is enabled, we turn it off
  useEffect(() => {
    if (
      draft.shortenLinksToggle === true &&
      linkShorteningDisabledOverride &&
      draft.urls.length === 0 &&
      draft.unshortenedUrls.length === 0
    ) {
      ComposerActionCreators.updateShouldShortenLinks(draft.id, false)
      dispatch(
        changeLinkShortenerThunk({
          profileId: selectedProfileId,
          domain: LinkShorteningDomains.NoShortening,
        }),
      )
    }
  }, [
    dispatch,
    draft.id,
    draft.shortenLinksToggle,
    linkShorteningDisabledOverride,
    draft.unshortenedUrls.length,
    draft.urls.length,
    selectedProfileId,
  ])

  useEffect(() => {
    function hasSelectedProfileEnabledShortening(): boolean {
      return (
        domainForLinkShorteningSelectedProfile !==
        LinkShorteningDomains.NoShortening
      )
    }

    const isFirstRender = !hasBeenRendered.current

    if (isFirstRender) {
      hasBeenRendered.current = true

      // If the draft already has a shortening toggle, use that, otherwise use the selected profile
      const shouldShorten =
        draft.shortenLinksToggle || hasSelectedProfileEnabledShortening()
      ComposerActionCreators.updateShouldShortenLinks(draft.id, shouldShorten)
      ComposerActionCreators.updateRegenerateShortenedLinks(
        draft.id,
        shouldShorten,
      )
    } else {
      dispatch(
        changeLinkShortenerThunk({
          profileId: selectedProfileId,
          domain: draft.shortenLinksToggle
            ? LinkShorteningDomains.DefaultShortener
            : LinkShorteningDomains.NoShortening,
        }),
      )
    }
  }, [])

  const onToggleLinkShortener = (
    event: React.MouseEvent<HTMLElement>,
  ): void => {
    event.preventDefault()

    const newStatusShortenLinks = !draft.shortenLinksToggle

    ComposerActionCreators.updateShouldShortenLinks(
      draft.id,
      newStatusShortenLinks,
    )
    ComposerActionCreators.updateRegenerateShortenedLinks(
      draft.id,
      newStatusShortenLinks,
    )

    setShowMessage(true)

    setTimeout(() => {
      setShowMessage(false) // Hide the message after a delay

      // track the event if the toggle is turned on/off
      const isShortened = draft.shortenLinksToggle
      trackLinkShortenerToggleClick(isShortened)
    }, 1500)
  }

  const shouldDisplayLinkShortenerButton =
    !draft.isStoryPost() &&
    !draft.service.isOmni &&
    // If the toggle is off and the link shortening is disabled override, don't show the button
    // we need to check for the toggle being off to preserve link shortenong for old posts
    !(
      draft.shortenLinksToggle === false &&
      linkShorteningDisabledOverride === true &&
      draft.shortLinkLongLinkMap.size === 0
    )

  if (!shouldDisplayLinkShortenerButton) return null

  const buttonClassName = `${styles.linkShortenerButton} ${
    draft.shortenLinksToggle ? styles.enabled : ''
  } ${showMessage ? styles.showMessage : ''}`

  const messageClassName = `${styles.message} ${
    showMessage ? styles.shouldShow : ''
  }`

  return (
    <Tooltip
      content={
        'Link Shortening is ' + (draft.shortenLinksToggle ? 'on' : 'off')
      }
      sideOffset={5}
      data-state="instant-open"
    >
      <Button
        className={buttonClassName}
        data-testid="link-shortening-icon"
        onClick={onToggleLinkShortener}
        data-tip={
          'Link Shortening is ' + (draft.shortenLinksToggle ? 'on' : 'off')
        }
        variant="tertiary"
        aria-label="Link shortening"
      >
        <LinkShortenerIcon isDisabled={!draft.shortenLinksToggle} />
        <span className={messageClassName}>
          {'Shortening ' + (draft.shortenLinksToggle ? 'on' : 'off')}{' '}
        </span>
      </Button>
    </Tooltip>
  )
}
